import { useCallback, useEffect, useReducer, useRef } from "react";

// Chrome/Firefox/Edge use the unprefixed API; older Safari needs the webkit-prefixed one.
const FULLSCREEN_CHANGE_EVENT = "onfullscreenchange" in document ? "fullscreenchange" : "webkitfullscreenchange";

function getFullscreenElement() {
    return document.fullscreenElement || document.webkitFullscreenElement || null;
}

function isFullscreenApiSupported() {
    return Boolean(document.fullscreenEnabled || document.webkitFullscreenEnabled);
}

function requestElementFullscreen(el) {
    if (!el) return Promise.reject(new Error("No element to fullscreen"));
    if (el.requestFullscreen) return el.requestFullscreen();
    if (el.webkitRequestFullscreen) return el.webkitRequestFullscreen();
    return Promise.reject(new Error("Fullscreen API not supported"));
}

function exitDocumentFullscreen() {
    if (document.exitFullscreen) return document.exitFullscreen();
    if (document.webkitExitFullscreen) return document.webkitExitFullscreen();
    return Promise.resolve();
}

// Best-effort devtools-open heuristic: a docked devtools panel inflates the gap between the
// outer window and the inner viewport. Not reliable on every browser/OS, hence the generous
// threshold and the "suspected" naming — this is a deterrent signal, not a guarantee.
const DEVTOOLS_SIZE_THRESHOLD = 160;
const DEVTOOLS_POLL_INTERVAL_MS = 1500;

function reducer(state, action) {
    switch (action.type) {
        case "SET_FULLSCREEN":
            return { ...state, isFullscreen: action.payload };
        case "ADD_VIOLATION": {
            const violation = { type: action.payload, timestamp: Date.now() };

            return {
                ...state,
                violations: [...state.violations, violation],
                lastViolation: violation,
            };
        }
        default:
            return state;
    }
}

/**
 * Tracks "secure exam mode" state for an active exam screen: fullscreen requirement,
 * tab-switch / fullscreen-exit / copy-paste / blocked-shortcut / suspected-devtools
 * violations, and (unchanged from the previous per-screen behaviour) silently trapping
 * right-click and the browser back button.
 *
 * Frontend-only: nothing here is sent to the server, state resets on reload.
 */
export default function useSecureExamMode({ enabled = false } = {}) {
    const [state, dispatch] = useReducer(reducer, {
        isFullscreen: false,
        violations: [],
        lastViolation: null,
    });

    // Only count a fullscreen exit as a violation if we were actually in fullscreen before
    // (avoids a false "violation" firing from the initial fullscreenchange listener setup).
    const wasFullscreenRef = useRef(false);

    const requestFullscreen = useCallback(() => {
        return requestElementFullscreen(document.documentElement).catch(() => {});
    }, []);

    const exitFullscreen = useCallback(() => {
        if (getFullscreenElement()) {
            return exitDocumentFullscreen().catch(() => {});
        }
        return Promise.resolve();
    }, []);

    useEffect(() => {
        if (!enabled) return undefined;

        const handleFullscreenChange = () => {
            const isFs = Boolean(getFullscreenElement());

            dispatch({ type: "SET_FULLSCREEN", payload: isFs });

            if (!isFs && wasFullscreenRef.current) {
                dispatch({ type: "ADD_VIOLATION", payload: "fullscreen-exit" });
            }

            wasFullscreenRef.current = isFs;
        };

        const handleVisibilityChange = () => {
            if (document.hidden) {
                dispatch({ type: "ADD_VIOLATION", payload: "tab-switch" });
            }
        };

        // Silently block the context menu (matches prior per-screen behaviour, not counted
        // as a violation - a stray right-click isn't meaningfully "leaving" the exam).
        const handleContextMenu = (e) => {
            e.preventDefault();
        };

        // Silently trap the back button (matches prior per-screen behaviour).
        const handlePopState = () => {
            window.history.go(1);
        };

        const handleCopyPaste = (e) => {
            e.preventDefault();
            dispatch({ type: "ADD_VIOLATION", payload: "copy-paste" });
        };

        const handleKeyDown = (e) => {
            const key = (e.key || "").toLowerCase();
            const isMod = e.ctrlKey || e.metaKey;

            const isBlocked =
                key === "f12" ||
                (isMod && e.shiftKey && ["i", "j", "c"].includes(key)) ||
                (isMod && !e.shiftKey && ["u", "p", "s"].includes(key));

            if (isBlocked) {
                e.preventDefault();
                dispatch({ type: "ADD_VIOLATION", payload: "blocked-shortcut" });
            }
        };

        document.addEventListener(FULLSCREEN_CHANGE_EVENT, handleFullscreenChange);
        document.addEventListener("visibilitychange", handleVisibilityChange);
        document.addEventListener("contextmenu", handleContextMenu);
        document.addEventListener("copy", handleCopyPaste);
        document.addEventListener("cut", handleCopyPaste);
        document.addEventListener("paste", handleCopyPaste);
        document.addEventListener("keydown", handleKeyDown);
        window.addEventListener("popstate", handlePopState);

        let devtoolsSuspected = false;
        const devtoolsIntervalId = setInterval(() => {
            const widthDiff = window.outerWidth - window.innerWidth;
            const heightDiff = window.outerHeight - window.innerHeight;
            const suspected = widthDiff > DEVTOOLS_SIZE_THRESHOLD || heightDiff > DEVTOOLS_SIZE_THRESHOLD;

            if (suspected && !devtoolsSuspected) {
                dispatch({ type: "ADD_VIOLATION", payload: "devtools-suspected" });
            }

            devtoolsSuspected = suspected;
        }, DEVTOOLS_POLL_INTERVAL_MS);

        return () => {
            document.removeEventListener(FULLSCREEN_CHANGE_EVENT, handleFullscreenChange);
            document.removeEventListener("visibilitychange", handleVisibilityChange);
            document.removeEventListener("contextmenu", handleContextMenu);
            document.removeEventListener("copy", handleCopyPaste);
            document.removeEventListener("cut", handleCopyPaste);
            document.removeEventListener("paste", handleCopyPaste);
            document.removeEventListener("keydown", handleKeyDown);
            window.removeEventListener("popstate", handlePopState);
            clearInterval(devtoolsIntervalId);

            // Don't leave the student stuck in fullscreen once secure mode turns off.
            exitFullscreen();
        };
    }, [enabled, exitFullscreen]);

    return {
        isFullscreen: state.isFullscreen,
        fullscreenSupported: isFullscreenApiSupported(),
        requestFullscreen,
        exitFullscreen,
        violationCount: state.violations.length,
        lastViolation: state.lastViolation,
    };
}
